/**
 * Stores > Inventory Count > session at /stores/inventory/:id.
 *
 * The counting loop itself (scanner, entry form, add-or-replace, receiving
 * checklist, counted list, submit/discard) is `CountSession` from
 * `@ampersandbrands/ui`, shared with The Sweet Spot. This file is the fetch,
 * the store-scoping (?store_no= for an HQ user), and the four IO functions
 * the shared component calls: look up a code, save a count, delete a line,
 * discard or submit the session.
 *
 * Gated by stores:store; the backend scopes every call to the caller's store.
 */

import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { CountSession as SharedCountSession } from '@ampersandbrands/ui';
import {
  useCountSession,
  useItemLookup,
  useUpsertCountLine,
  useDeleteCountLine,
  useDeleteCountSession,
  useSubmitCountSession,
  hasStoresStore,
  isNotAvailable,
  isUnmapped,
} from '@/lib/useStores';
import { useSession } from '@/lib/useSession';
import { AccessDeniedCard, UnmappedCard, NotAvailableCard, ErrorCard } from './storesShared';
import { readInventoryStoreParam } from './inventoryStoreParam';

export function CountSession() {
  const { id } = useParams();
  const sessionId = Number(id);
  const navigate = useNavigate();
  const { user } = useSession();
  const canView = hasStoresStore(user?.permissions, user?.superAdmin);

  // HQ users act on the store carried in ?store= (set by the Inventory Count
  // page when a store is picked; ?store_no= is read as a fallback for old
  // links). Without it the backend scopes to the caller's own mapped store,
  // which is the wrong store for an HQ-started session.
  const [searchParams] = useSearchParams();
  const storeNoParam = readInventoryStoreParam(searchParams);
  const storeNo = storeNoParam ?? undefined;

  const detailQ = useCountSession(Number.isFinite(sessionId) ? sessionId : undefined, storeNo);
  const lookupM = useItemLookup();
  const upsertM = useUpsertCountLine(sessionId);
  const deleteLineM = useDeleteCountLine(sessionId);
  const deleteSessionM = useDeleteCountSession();
  const submitM = useSubmitCountSession(sessionId);

  // Preserve the HQ ?store= across every navigation back to the counting
  // home, so an HQ user who picked a store does not lose it.
  const backHref = `/stores/inventory${storeNo !== undefined ? `?store=${storeNo}` : ''}`;

  const session = detailQ.data?.session;
  const lines = detailQ.data?.lines ?? [];
  const poLines = detailQ.data?.po_lines ?? [];

  if (user && !canView) return <AccessDeniedCard what="Inventory Count" />;
  if (isUnmapped(detailQ.error)) {
    return <UnmappedCard />;
  }
  if (isNotAvailable(detailQ.error)) {
    return <NotAvailableCard what="Inventory Count" />;
  }

  function goBack() {
    // An open session with nothing scanned is not worth keeping.
    if (session && session.status !== 'submitted' && lines.length === 0) {
      deleteSessionM.mutate({ sessionId: session.id, store_no: storeNo }, { onSettled: () => navigate(backHref) });
      return;
    }
    navigate(backHref);
  }

  return (
    <SharedCountSession
      session={session ?? null}
      lines={lines}
      poLines={poLines}
      loading={detailQ.isLoading}
      error={detailQ.error ? <ErrorCard message={detailQ.error.message} /> : undefined}
      onBack={goBack}
      onLookup={async (code) => {
        const d = await lookupM.mutateAsync({ barcode: code, ...(storeNo !== undefined ? { store_no: storeNo } : {}) });
        return d.found ? d.item : null;
      }}
      onSaveCount={async ({ item_no, barcode, qty, mode }) => {
        await upsertM.mutateAsync({
          item_no,
          barcod: barcode ?? undefined,
          qty,
          mode,
          ...(storeNo !== undefined ? { store_no: storeNo } : {}),
        });
      }}
      saveError={upsertM.error ? <div className="mt-2"><ErrorCard message={upsertM.error.message} /></div> : undefined}
      onDeleteLine={(lineId) => deleteLineM.mutate({ lineId, store_no: storeNo })}
      deletingLine={deleteLineM.isPending}
      deleteLineError={deleteLineM.error ? <div className="mt-2"><ErrorCard message={deleteLineM.error.message} /></div> : undefined}
      onDiscard={() => deleteSessionM.mutate({ sessionId, store_no: storeNo }, { onSuccess: () => navigate(backHref) })}
      discarding={deleteSessionM.isPending}
      discardError={deleteSessionM.error ? <div className="mt-2"><ErrorCard message={deleteSessionM.error.message} /></div> : undefined}
      onSubmit={() =>
        submitM.mutate(storeNo !== undefined ? { store_no: storeNo } : {}, { onSuccess: () => navigate(backHref) })
      }
      submitting={submitM.isPending}
      submitError={submitM.error ? <div className="mt-2"><ErrorCard message={submitM.error.message} /></div> : undefined}
    />
  );
}
